import { Edge, Node } from '@xyflow/react';

export enum StepType {
  SPECIALIZED = 'specialized',
  CONDITIONAL = 'conditional',
  FOR_LOOP = 'for_loop',
  NESTED_WORKFLOW = 'nested_workflow',
}

export enum MatchType {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  GREATER_THAN_OR_EQUALS = 'greater_than_or_equals',
  LESS_THAN_OR_EQUALS = 'less_than_or_equals',
  REGEX = 'regex',
}

export enum TriggerType {
  WEBHOOK = 'webhook',
  SCHEDULED = 'scheduled',
  MESSAGE = 'message',
  EMAIL = 'email',
}

export enum EmailFilterType {
  CONTAINS = 'contains',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  REGEX = 'regex',
  EXACT_MATCH = 'exact_match',
}

export type EmailFilter = {
  type: EmailFilterType;
  value: string;
  case_sensitive?: boolean;
};

export type Condition = {
  matching_condition: string;
  match_type: MatchType;
  go_to_step_id: string;
};

export type BaseStep = {
  step_id: string;
  type: StepType;
  name: string;
  description?: string | null;
  next_step_id?: string | null;
  parameters?: Record<string, unknown>;
  retry_strategy?: Record<string, unknown>;
  timeout_seconds?: number | null;
};

export type SpecializedAgentStep = {
  type: StepType.SPECIALIZED;
  agent_id: string;
} & BaseStep;

export type ConditionalStep = {
  type: StepType.CONDITIONAL;
  evaluator_agent_id: string;
  conditions: Condition[];
} & BaseStep;

export type ForLoopStep = {
  type: StepType.FOR_LOOP;
  items_source: string;
  item_name: string;
  steps: Record<string, Step>; // Dictionary of nested steps (not array)
  max_iterations?: number | null;
  parallel?: boolean;
} & BaseStep;

export type WorkflowStep = {
  type: StepType.NESTED_WORKFLOW;
  workflow_id: string;
  message: string;
} & BaseStep;

export type Step = SpecializedAgentStep | ConditionalStep | ForLoopStep | WorkflowStep;

// --- TRIGGER MODELS ---

export type BaseTrigger = {
  id: string;
  type: TriggerType;
  name: string;
  description?: string | undefined;
};

export type WebhookTrigger = {
  type: TriggerType.WEBHOOK;
  payload_content_type: string;
  payload_schema?: string | null;
  auth_key?: string;
  auth_header_name?: string;
  auth_header_format?: string;
  is_active?: boolean;
  created_at?: string;
  created_by?: string | null;
  invocation_count?: number;
  last_invocation?: string | null;
  links?: HateoasLinks;
} & BaseTrigger;

export type ScheduledTrigger = {
  type: TriggerType.SCHEDULED;
  schedule_type: string;
  scheduled_time?: string | null;
  cron_expression?: string | null;
  timezone?: string;
  message?: string | null;
  max_occurrences?: number | null;
  end_date?: string | null;
  workflow_agent_id: string;
  version: number;
  is_active: boolean;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  end_type?: string;
  links?: HateoasLinks;
} & BaseTrigger;

export type MessageTrigger = {
  type: TriggerType.MESSAGE;
  workflow_agent_id?: string;
  is_active?: boolean; // defaults to true
  created_at?: string;
  created_by?: string | null;
  links?: HateoasLinks;
} & BaseTrigger;

export type HateoasLinks = {
  self: string;
  collection: string;
  workflow_agent: string;
  update: string;
  delete: string;
  management: string;
};

export type EmailTrigger = {
  type: TriggerType.EMAIL;
  workflow_agent_id: string;
  recipient: string;
  email_address: string;
  subject_filters?: EmailFilter[];
  sender_filters?: EmailFilter[];
  auto_response_enabled?: boolean;
  auto_response_template?: string;
  attachment_processing?: boolean;
  max_email_size_mb?: number;
  ses_registered: boolean;
  is_active: boolean;
  version: number;
  invocation_count: number;
  last_invocation: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  emails_received_count: number;
  last_email_received: string | null;
  links?: HateoasLinks;
} & BaseTrigger;

export type Trigger = WebhookTrigger | ScheduledTrigger | MessageTrigger | EmailTrigger;

// Type guard functions for trigger types
export const isEmailTrigger = (trigger: Trigger): trigger is EmailTrigger => {
  return trigger.type === TriggerType.EMAIL;
};

export const isWebhookTrigger = (trigger: Trigger): trigger is WebhookTrigger => {
  return trigger.type === TriggerType.WEBHOOK;
};

export const isScheduleTrigger = (trigger: Trigger): trigger is ScheduledTrigger => {
  return trigger.type === TriggerType.SCHEDULED;
};

export const isMessageTrigger = (trigger: Trigger): trigger is MessageTrigger => {
  return trigger.type === TriggerType.MESSAGE;
};

export type WorkflowAgent = {
  id: string;
  name: string;
  agent_id: string;
  description?: string | null;
  version?: string | null;
  objective?: string | null;
  plan?: string | null;
  steps: Record<string, Step>;
  start_step_id: string;
  variables?: Record<string, unknown>;
  triggers?: Trigger[];
  created_at: string; // ISO date string
  updated_at: string; // ISO date string
  created_by?: string | null;
  is_active: boolean;
  is_deleted: boolean;
  nodes?: Node[];
  edges?: Edge[];
  agent_metadata?: Record<string, unknown> | null;
  links?: {
    self: string;
    collection: string;
    update: string;
    patch: string;
    delete: string;
    toggle_active: string;
    trigger: string;
    executions: string;
  };
};

export type WorkflowAgentExecution = {
  links: {
    self: string;
    collection: string;
    workflow_agent: string;
  };
  id: string;
  workflow_agent_id: string;
  status: string;
  payload: {
    text_content: string;
  };
  result?: {
    response: string;
    token_usage?: {
      input_tokens: number;
      total_tokens: number;
      output_tokens: number;
    };
    execution_time?: number;
  };
  error_message?: string | null;
  start_time: string;
  end_time?: string | null;
  user_id: string;
  trigger_id?: string | null;
  step_executions: unknown[];
  opal_instance_id: string;
};

export type WorkflowAgentTriggerResponse = {
  id: string;
  status: string;
  [key: string]: unknown;
};
